/**
 * Prenotazioni API Queries
 * Centralized data fetching for Prenotazioni feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Prenotazione, PrenotazioneInsert, PrenotazioneUpdate,
  QueryOptions 
} from '@/types';
import { DatabaseTable } from '@/types/database.types';
import { formatISO, startOfDay, endOfDay, format } from 'date-fns';

const TABLE = DatabaseTable.PRENOTAZIONI;

// ============================================================
// GET Queries
// ============================================================

export async function getPrenotazioni(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*, properties(nome)', { count: options?.count })
    .eq('user_id', userId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  } else {
    query = query.order('data_checkin', { ascending: false });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  return {
    data: data as (Prenotazione & { properties: { nome: string } | null })[] | null,
    error,
    count,
  };
}

export async function getPrenotazioneById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, properties(*)')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return {
    data: data as (Prenotazione & { properties: unknown }) | null,
    error,
  };
}

export async function getPrenotazioniAttive() {
  return getPrenotazioni({
    filters: { 
      eq: { stato: 'confermata' },
      gte: { data_checkout: formatISO(new Date()) }
    },
    order: { column: 'data_checkin', ascending: true },
  });
}

export async function getPrenotazioniInArrivoOggi() {
  const today = format(new Date(), 'yyyy-MM-dd');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, properties(nome)')
    .eq('data_checkin', today)
    .eq('stato', 'confermata');

  return {
    data: data as (Prenotazione & { properties: { nome: string } | null })[] | null,
    error,
  };
}

export async function getPrenotazioniInArrivo(giorni: number = 7, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const oggi = format(new Date(), 'yyyy-MM-dd');
  const futuro = format(new Date(Date.now() + giorni * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, properties(nome)')
    .eq('user_id', uid)
    .gte('data_checkin', oggi)
    .lte('data_checkin', futuro)
    .eq('stato', 'confermata')
    .order('data_checkin', { ascending: true });

  return {
    data: data as (Prenotazione & { properties: { nome: string } | null })[] | null,
    error,
  };
}

export async function getPrenotazioniInPartenzaOggi() {
  const today = format(new Date(), 'yyyy-MM-dd');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, properties(nome)')
    .eq('data_checkout', today)
    .eq('stato', 'confermata');

  return {
    data: data as (Prenotazione & { properties: { nome: string } | null })[] | null,
    error,
  };
}

export async function getPrenotazioniInPeriodo(dataInizio: Date, dataFine: Date) {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, properties(nome)')
    .gte('data_checkin', format(dataInizio, 'yyyy-MM-dd'))
    .lte('data_checkout', format(dataFine, 'yyyy-MM-dd'));

  return {
    data: data as (Prenotazione & { properties: { nome: string } | null })[] | null,
    error,
  };
}

// ============================================================
// POST Queries
// ============================================================

export async function createPrenotazione(prenotazione: PrenotazioneInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  let ospite_id = prenotazione.ospite_id;
  
  // Se non c'è ospite_id, cerca o crea ospite automaticamente
  if (!ospite_id && (prenotazione.ospite_email || prenotazione.ospite_nome)) {
    const { data: existingOspiti } = await supabase
      .from(DatabaseTable.OSPITI)
      .select('id, email, nome, cognome')
      .eq('user_id', uid)
      .or(prenotazione.ospite_email 
        ? `email.eq.${prenotazione.ospite_email}`
        : `nome.ilike.%${prenotazione.ospite_nome}%`)
      .limit(1);
    
    if (existingOspiti && existingOspiti.length > 0) {
      // Usa ospite esistente
      ospite_id = existingOspiti[0].id;
    } else if (prenotazione.ospite_nome) {
      // Crea nuovo ospite
      const nomeParts = prenotazione.ospite_nome.split(' ');
      const nome = nomeParts[0] || '';
      const cognome = nomeParts.slice(1).join(' ') || '';
      
      const { data: newOspite } = await supabase
        .from(DatabaseTable.OSPITI)
        .insert({
          user_id: uid,
          nome,
          cognome,
          email: prenotazione.ospite_email || null,
          telefono: prenotazione.ospite_telefono || null,
        })
        .select()
        .single();
      
      if (newOspite) {
        ospite_id = newOspite.id;
      }
    }
  }
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...prenotazione, user_id: uid, ospite_id })
    .select()
    .single();

  return {
    data: data as Prenotazione | null,
    error,
  };
}

// ============================================================
// PATCH Queries
// ============================================================

export async function updatePrenotazione(id: string, updates: PrenotazioneUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return {
    data: data as Prenotazione | null,
    error,
  };
}

// ============================================================
// DELETE Queries
// ============================================================

export async function deletePrenotazione(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// Stats & Analytics
// ============================================================

export async function getPrenotazioniStats() {
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const monthStart = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
  const monthEnd = format(new Date(today.getFullYear(), today.getMonth() + 1, 0), 'yyyy-MM-dd');

  // Prenotazioni attive
  const { count: attive, error: errorAttive } = await supabase
    .from(TABLE)
    .select('*', { count: 'exact', head: true })
    .eq('stato', 'confermata')
    .gte('data_checkout', todayStr);

  // Arrivi oggi
  const { count: arrivi, error: errorArrivi } = await supabase
    .from(TABLE)
    .select('*', { count: 'exact', head: true })
    .eq('data_checkin', todayStr)
    .eq('stato', 'confermata');

  // Partenze oggi
  const { count: partenze, error: errorPartenze } = await supabase
    .from(TABLE)
    .select('*', { count: 'exact', head: true })
    .eq('data_checkout', todayStr)
    .eq('stato', 'confermata');

  // Fatturato mese
  const { data: fatturatoData, error: errorFatturato } = await supabase
    .from(TABLE)
    .select('importo_totale')
    .gte('data_checkin', monthStart)
    .lte('data_checkin', monthEnd)
    .eq('stato', 'confermata');

  const fatturatoMese = fatturatoData?.reduce((sum: number, p: { importo_totale: number | null }) => sum + (p.importo_totale || 0), 0) || 0;

  return {
    data: {
      prenotazioniAttive: attive || 0,
      arriviOggi: arrivi || 0,
      partenzeOggi: partenze || 0,
      fatturatoMese,
    },
    error: errorAttive || errorArrivi || errorPartenze || errorFatturato,
  };
}

// ============================================================
// Availability Check
// ============================================================

export async function checkAvailability(
  strutturaId: string, 
  dataCheckin: Date, 
  dataCheckout: Date,
  excludePrenotazioneId?: string
) {
  let query = supabase
    .from(TABLE)
    .select('*')
    .eq('struttura_id', strutturaId)
    .eq('stato', 'confermata')
    .lt('data_checkin', format(dataCheckout, 'yyyy-MM-dd'))
    .gt('data_checkout', format(dataCheckin, 'yyyy-MM-dd'));

  if (excludePrenotazioneId) {
    query = query.neq('id', excludePrenotazioneId);
  }

  const { data, error } = await query;

  return {
    available: !data || data.length === 0,
    conflictingPrenotazioni: data as Prenotazione[] | null,
    error,
  };
}
